// this file allow font scaling inside webviews
import 'platform-specific/setup';

import type { PartialTheme } from '@repo/theme';
import { ThemeProvider } from '@repo/theme';
import { RenderNotificationToasts } from 'component/notification-toast';
import { getTheme } from 'config/theme';
import { Slot } from 'expo-router';
import { OverlayPortalBoundary } from 'platform-specific/overlay-portal';
import { useRef } from 'react';
import { registerLoginProviders } from 'structure/login';
import { ApiClientProvider } from 'system/api-client-context';
import { Catcher } from 'system/catcher';
import { createApiClient, createApiQueryUtils, createOrpcQueryClient } from 'system/orpc';
import { AppData } from 'util/appdata';
import { AppDataContext } from 'util/appdataContext';
import { authClient } from 'util/auth-client';
import {
    auth0Login,
    cbcIntLogin,
    cbcLogin,
    eoLogin,
    KroNcrvNpoIdTestLogin,
    kikaLogin,
    rcIntLogin,
    rcLogin,
    rtbfLogin,
    srgIntLogin,
    stubOidcFragmentLogin,
    stubOidcLogin,
    stubOidcRedirectLogin,
    zdfIntLogin,
    zdfProdLogin,
} from 'util/loginproviders';
import { dropAbandonedPendingLoginAction } from 'util/pending-login-action';
import { useStandardFonts } from 'util/standard-fonts';
import { useSSOLoginFromParent } from 'util/useSSOLoginFromParent';
import { getDeploymentConfig } from 'util/util';

const loginProviderList = [
    rcIntLogin,
    rcLogin,
    cbcLogin,
    cbcIntLogin,
    srgIntLogin,
    zdfIntLogin,
    zdfProdLogin,
    kikaLogin,
    rtbfLogin,
    KroNcrvNpoIdTestLogin,
    eoLogin,
];

// Register test-only providers in development environments.
if (getDeploymentConfig().enableDevLogin) {
    loginProviderList.push(auth0Login);
    loginProviderList.push(stubOidcLogin);
    loginProviderList.push(stubOidcFragmentLogin);
    loginProviderList.push(stubOidcRedirectLogin);
}

registerLoginProviders(loginProviderList);
dropAbandonedPendingLoginAction();

// Expose auth client on window for E2E tests to authenticate via puppet login
// without navigating away from the current page. Access is gated by the puppet
// secret, not by this exposure.
(window as any).__testAuthClient = authClient;

// Resolve the URL/silo theme once — the shell is the sanctioned impure
// callsite. `fontLoader` stays a shell concern (useStandardFonts), so only
// the token groups cross into the provider.
function resolveAppTheme(): PartialTheme | undefined {
    const legacyTheme = getTheme();
    if (!legacyTheme) return undefined;
    return {
        colors: legacyTheme.colors,
        fonts: legacyTheme.fontFamilies as PartialTheme['fonts'],
        avatarColors: legacyTheme.avatarColors,
    };
}

const appTheme = resolveAppTheme();

export default function RootLayout() {
    const appData = useRef(new AppData()).current;
    const apiClient = useRef(createApiClient()).current;
    const queryClient = useRef(createOrpcQueryClient()).current;
    const apiQueryUtils = useRef(createApiQueryUtils(apiClient)).current;
    useStandardFonts();
    useSSOLoginFromParent();
    return (
        <Catcher>
            <ThemeProvider theme={appTheme}>
                <OverlayPortalBoundary>
                    <ApiClientProvider apiClient={apiClient} apiQueryUtils={apiQueryUtils} queryClient={queryClient}>
                        <AppDataContext.Provider value={appData}>
                            <Slot />
                            <RenderNotificationToasts />
                        </AppDataContext.Provider>
                    </ApiClientProvider>
                </OverlayPortalBoundary>
            </ThemeProvider>
        </Catcher>
    );
}
